import React, { useEffect, useState } from 'react';
import CommandCenter from './pages/CommandCenter';
import Dashboard from './pages/Dashboard';
import EmailPerformance from './pages/EmailPerformance';
import Revenue from './pages/Revenue';
import Tracking from './pages/Tracking';
import Customers from './pages/Customers';
import Drafts from './pages/Drafts';
import Funnels from './pages/Funnels';
import Settings from './pages/Settings';

/**
 * Tab navigation structure — ordered by operator priority
 * Command Center = daily snapshot
 * Legacy Dashboard = detailed email performance view (kept as-is)
 */
const TABS = [
  { id: 'command-center', label: 'Command Center', icon: '⚡' },
  { id: 'email',          label: 'Email',          icon: '📧' },
  { id: 'revenue',        label: 'Revenue',        icon: '💰' },
  { id: 'tracking',       label: 'Tracking',       icon: '📍' },
  { id: 'customers',      label: 'Customers',      icon: '👥' },
  { id: 'funnels',        label: 'Funnels',        icon: '🧲' },
  { id: 'drafts',         label: 'Compose',        icon: '✍️' },
  { id: 'settings',       label: 'Settings',       icon: '⚙️' },
];

export default function App() {
  const [activeTab, setActiveTab] = useState('command-center');
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth <= 720 : false);
  const activePage = TABS.find(tab => tab.id === activeTab) || TABS[0];

  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth <= 720);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Top Nav Bar */}
      <header style={{
        borderBottom: '1px solid var(--border)',
        padding: isMobile ? '12px 16px' : '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexDirection: isMobile ? 'column' : 'row',
        flexWrap: isMobile ? 'nowrap' : 'wrap',
        gap: isMobile ? 8 : 12,
        minHeight: 56,
        height: 'auto',
        position: 'sticky',
        top: 0,
        background: 'var(--bg)',
        zIndex: 100,
        overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexShrink: 0, width: isMobile ? '100%' : 'auto' }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 7,
              background: 'linear-gradient(135deg, var(--blue), var(--green))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, flexShrink: 0,
            }}>⚡</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}>ECC</div>
              <div style={{ fontSize: 10, color: 'var(--muted)', fontFamily: "'JetBrains Mono', monospace", lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Freedom Income Options
              </div>
            </div>
          </div>

          {isMobile && (
            <label style={{ position: 'relative', display: 'flex', alignItems: 'center', minWidth: 154, flex: '0 0 154px' }}>
              <span style={{ position: 'absolute', left: 12, pointerEvents: 'none', fontSize: 14 }}>{activePage.icon}</span>
              <select
                aria-label="Select ECC page"
                value={activeTab}
                onChange={e => setActiveTab(e.target.value)}
                style={{
                  width: '100%',
                  height: 38,
                  padding: '0 32px 0 34px',
                  borderRadius: 9,
                  border: '0.5px solid #2a2a2a',
                  background: '#141414',
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: 700,
                  fontFamily: "'DM Sans', sans-serif",
                  outline: 'none',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  cursor: 'pointer',
                }}
              >
                {TABS.map(tab => (
                  <option key={tab.id} value={tab.id}>{tab.label}</option>
                ))}
              </select>
              <span style={{ position: 'absolute', right: 12, pointerEvents: 'none', color: 'var(--muted)', fontSize: 10 }}>▼</span>
            </label>
          )}
        </div>

        {/* Tab Navigation */}
        {!isMobile && <nav style={{
          display: 'flex',
          gap: 0,
          alignItems: 'stretch',
          height: 56,
          flex: '1 1 0',
          minWidth: 0,
          width: 'auto',
          maxWidth: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '0 16px',
                fontSize: 13,
                fontWeight: 500,
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid var(--blue)' : '2px solid transparent',
                borderTop: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                cursor: 'pointer',
                background: 'transparent',
                color: activeTab === tab.id ? '#fff' : 'var(--muted)',
                transition: 'color 0.15s, border-color 0.15s',
                fontFamily: "'DM Sans', sans-serif",
                whiteSpace: 'nowrap',
                flex: '0 0 auto',
              }}
            >
              <span style={{ fontSize: 14 }}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>}
      </header>

      {/* Page Content */}
      <main style={{ padding: isMobile ? '24px 16px' : '28px 24px', maxWidth: 1400, margin: '0 auto' }}>
        {activeTab === 'command-center' && <CommandCenter />}
        {activeTab === 'email'          && <EmailPerformance />}
        {activeTab === 'revenue'        && <Revenue />}
        {activeTab === 'tracking'       && <Tracking />}
        {activeTab === 'customers'      && <Customers />}
        {activeTab === 'funnels'        && <Funnels />}
        {activeTab === 'drafts'         && <Drafts />}
        {activeTab === 'settings'       && <Settings />}
      </main>
    </div>
  );
}
